// BuildingCard — the shared-location card (Phase 3, 2026-07-17).
//
// Renders a building's members either CONSOLIDATED (one card, suites table,
// pinned members starred; single-tenant collapses to a chip strip) or SPLIT
// (per-member mini-cards cross-referencing "shares this location with →").
// The header toggle writes ONLY display prefs: tapping sets the caller's
// personal override (user_building_view_prefs); "Make team default" writes
// the building's shared default. Billing renders read-only in
// BilledTogetherPanel. NOTHING here can write service_assignments.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Star } from 'lucide-react';
import { useBuildingOverview } from '../use-building-overview';
import type { BuildingMember, ResolvedDisplayMode } from '../types';
import { BilledTogetherPanel } from './billed-together-panel';
import { SuiteDrawer } from './suite-drawer';

const RAG_DOT: Record<string, string> = {
  Green: 'bg-st-ok-solid',
  Amber: 'bg-st-warn-solid',
  Red: 'bg-st-danger-solid',
};

function RagDot({ rag }: { rag: string | null }) {
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${RAG_DOT[rag ?? ''] ?? 'bg-tcs-border'}`}
      aria-label={rag ?? 'No RAG'}
      title={rag ?? 'No RAG'}
    />
  );
}

// Axis chips — make the three axes visibly distinct (location/billing/feedback)
export function AxisChip({ axis }: { axis: 'location' | 'billing' | 'feedback' }) {
  const styles = {
    location: 'bg-st-info-solid/10 text-st-info-fg border-st-info-border-strong/30',
    billing: 'bg-violet-500/10 text-violet-600 border-violet-500/30',
    feedback: 'bg-st-ok-solid/10 text-st-ok-fg border-st-ok-border-strong/30',
  } as const;
  const label = { location: 'Shared location', billing: 'Billed together', feedback: 'Reports separately' }[axis];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles[axis]}`}>
      {label}
    </span>
  );
}

export function BuildingCard({
  buildingId,
  currentFacilityId,
}: {
  buildingId: string;
  currentFacilityId?: string;
}) {
  const { overview, loading, error, setBuildingDisplayDefault, setMyDisplayOverride } =
    useBuildingOverview(buildingId);
  const [drawerFacilityId, setDrawerFacilityId] = useState<string | null>(null);
  const [savingMode, setSavingMode] = useState(false);

  if (loading && !overview) {
    return (
      <div className="animate-pulse rounded-xl border border-tcs-border bg-tcs-bg p-5">
        <div className="h-4 w-48 rounded bg-tcs-surface" />
        <div className="mt-3 h-3 w-72 rounded bg-tcs-surface" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5 text-sm text-tcs-text-secondary">
        Couldn't load shared-location info. {error}
      </div>
    );
  }
  if (!overview) return null;

  const { building, members, resolved_display_mode: mode } = overview;
  const singleTenant = overview.tenant_count <= 1;

  const switchMode = async (next: ResolvedDisplayMode) => {
    if (next === mode || savingMode) return;
    setSavingMode(true);
    try {
      await setMyDisplayOverride(next); // personal override — never finance truth
    } finally {
      setSavingMode(false);
    }
  };

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-tcs-border p-4 md:p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-tcs-text-secondary" />
            <h3 className="truncate text-sm font-semibold text-tcs-text">{building.building_name}</h3>
          </div>
          <p className="mt-1 text-xs text-tcs-text-secondary">
            {[building.address, building.city, building.state].filter(Boolean).join(', ')}
            {building.cluster_label ? ` · "${building.cluster_label}"` : ''}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <AxisChip axis="location" />
            {/* Only when an invoice really covers two or more facilities —
                Orbis's four suites are four invoices (Ben, 2026-10-19). */}
            {overview.has_shared_invoice && <AxisChip axis="billing" />}
            <AxisChip axis="feedback" />
          </div>
          <p className="mt-2 text-[11px] text-tcs-text-muted">
            {overview.member_count} facilities · {overview.tenant_count}{' '}
            {overview.tenant_count === 1 ? 'tenant' : 'tenants'}
            {overview.total_sqft ? ` · ${overview.total_sqft.toLocaleString()} SF` : ''}
          </p>
        </div>

        {/* Consolidated / Split toggle — display prefs only */}
        <div className="flex flex-col items-end gap-1">
          <div
            className="inline-flex overflow-hidden rounded-lg border border-tcs-border"
            role="tablist"
            aria-label="Display mode"
          >
            {(['consolidated', 'split'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                disabled={savingMode}
                onClick={() => void switchMode(m)}
                className={`min-h-[44px] px-3 text-xs font-semibold capitalize transition-colors ${
                  mode === m
                    ? 'bg-tcs-inverse text-tcs-bg'
                    : 'bg-tcs-bg text-tcs-text-secondary hover:bg-tcs-surface'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="text-[10px] text-tcs-text-muted underline-offset-2 hover:underline"
            onClick={() => void setBuildingDisplayDefault(mode)}
            title="Save the current view as this building's shared default for everyone"
          >
            {overview.display_mode_source === 'user' ? 'Make team default' : `Team default (${overview.display_mode_source})`}
          </button>
        </div>
      </div>

      {/* Members */}
      <div className="p-4 md:p-5">
        {mode === 'consolidated' ? (
          singleTenant && overview.suite_count > 0 ? (
            // Single tenant → suites collapse to a chip strip
            <div className="flex flex-wrap gap-2">
              {members.map((m) => (
                <button
                  key={m.facility_id}
                  type="button"
                  onClick={() => setDrawerFacilityId(m.facility_id)}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-tcs-border bg-tcs-surface px-3 text-xs text-tcs-text hover:border-tcs-text-secondary"
                >
                  <RagDot rag={m.account_status_rag} />
                  {m.suite ? `Suite ${m.suite}` : m.facility_name}
                  {m.is_pinned && <Star className="h-3 w-3 fill-st-warn-solid text-st-warn-solid" />}
                </button>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-tcs-border">
              {members.map((m) => (
                <li key={m.facility_id}>
                  <button
                    type="button"
                    onClick={() => setDrawerFacilityId(m.facility_id)}
                    className="flex min-h-[52px] w-full items-center gap-3 px-1 py-2 text-left hover:bg-tcs-surface"
                  >
                    <RagDot rag={m.account_status_rag} />
                    <span className="w-16 shrink-0 font-mono text-[11px] text-tcs-text-muted">
                      {m.suite ? `Ste ${m.suite}` : '—'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-tcs-text">
                        {m.facility_name}
                        {m.is_pinned && (
                          <Star className="ml-1 inline h-3.5 w-3.5 fill-st-warn-solid text-st-warn-solid" aria-label="Pinned — called out separately" />
                        )}
                        {m.is_common_area && (
                          <span className="ml-1 rounded bg-tcs-surface px-1 text-[9px] uppercase text-tcs-text-muted">common area</span>
                        )}
                      </span>
                      <span className="block truncate text-[11px] text-tcs-text-secondary">
                        {m.account_name ?? m.account_id ?? '—'}
                        {m.open_feedback_count > 0 && ` · ${m.open_feedback_count} open feedback`}
                      </span>
                    </span>
                    <span className="text-xs text-tcs-text-muted">›</span>
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <SplitCards members={members} currentFacilityId={currentFacilityId} onOpenSuite={setDrawerFacilityId} />
        )}
      </div>

      {/* Billed together — read-only rollup */}
      {overview.rollup_groups.length > 0 && (
        <div className="border-t border-tcs-border p-4 md:p-5">
          <BilledTogetherPanel overview={overview} />
        </div>
      )}

      <SuiteDrawer
        facilityId={drawerFacilityId}
        member={members.find((m) => m.facility_id === drawerFacilityId) ?? null}
        facilityNameFor={(id) => overview.facility_names[id] ?? id}
        onClose={() => setDrawerFacilityId(null)}
      />
    </div>
  );
}

// Split view — separate mini-cards, each cross-referencing its siblings.
function SplitCards({
  members,
  currentFacilityId,
  onOpenSuite,
}: {
  members: BuildingMember[];
  currentFacilityId?: string;
  onOpenSuite: (facilityId: string) => void;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {members.map((m) => {
        const siblings = members.filter((s) => s.facility_id !== m.facility_id);
        const isCurrent = m.facility_id === currentFacilityId;
        return (
          <div
            key={m.facility_id}
            className={`rounded-lg border p-3 ${
              isCurrent ? 'border-tcs-text-secondary bg-tcs-surface' : 'border-tcs-border bg-tcs-bg'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-tcs-text">
                  <RagDot rag={m.account_status_rag} />{' '}
                  {m.facility_name}
                  {m.is_pinned && (
                    <Star className="ml-1 inline h-3.5 w-3.5 fill-st-warn-solid text-st-warn-solid" />
                  )}
                </p>
                <p className="mt-0.5 truncate text-[11px] text-tcs-text-secondary">
                  {m.suite ? `Suite ${m.suite} · ` : ''}
                  {m.account_name ?? m.account_id ?? '—'}
                </p>
              </div>
              {isCurrent ? (
                <span className="rounded bg-tcs-inverse px-1.5 py-0.5 text-[9px] font-semibold uppercase text-tcs-bg">This one</span>
              ) : (
                <Link
                  to={`/account-management/facilities/${m.facility_id}`}
                  className="shrink-0 text-xs text-tcs-text-secondary underline-offset-2 hover:underline"
                >
                  Open →
                </Link>
              )}
            </div>
            <button
              type="button"
              onClick={() => onOpenSuite(m.facility_id)}
              className="mt-2 min-h-[44px] w-full rounded-lg border border-tcs-border bg-tcs-bg px-2 text-left text-[11px] text-tcs-text-secondary hover:bg-tcs-surface"
            >
              Quick view · {m.open_feedback_count} open feedback
              {m.total_sqft ? ` · ${m.total_sqft.toLocaleString()} SF` : ''}
            </button>
            {siblings.length > 0 && (
              <p className="mt-2 truncate text-[10px] text-tcs-text-muted">
                Shares this location with →{' '}
                {siblings.map((s) => s.facility_name).join(', ')}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
